import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    limit,
    arrayUnion,
    arrayRemove,
    increment
} from 'firebase/firestore';
import { db } from '../firebase/config';

// User Operations
export const getUser = async (userId) => {
    const userDoc = await getDoc(doc(db, 'users', userId));
    return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
};

export const updateUser = async (userId, updates) => {
    await updateDoc(doc(db, 'users', userId), {
        ...updates,
        updatedAt: new Date().toISOString()
    });
};

// Contribution Operations
export const getUserContributions = async (userId) => {
    try {
        const q = query(
            collection(db, 'contributions'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Ensure we have default values
            amount: doc.data().amount || 0,
            status: doc.data().status || 'completed',
            paymentMethod: doc.data().paymentMethod || 'wallet',
            createdAt: doc.data().createdAt || new Date().toISOString()
        }));
    } catch (error) {
        console.error('Error getting user contributions:', error);
        throw error;
    }
};

export const createContribution = async (contributionData) => {
    try {
        const contributionRef = await addDoc(collection(db, 'contributions'), {
            userId: contributionData.userId,
            userName: contributionData.userName,
            caseId: contributionData.caseId,
            caseMemberName: contributionData.caseMemberName,
            relationship: contributionData.relationship,
            amount: contributionData.amount,
            paymentMethod: contributionData.paymentMethod,
            voucherNumber: contributionData.voucherNumber,
            status: 'completed',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });

        // Update user's contribution count and balance
        await updateDoc(doc(db, 'users', contributionData.userId), {
            totalContributions: increment(1),
            availableBalance: increment(-contributionData.amount),
            updatedAt: new Date().toISOString()
        });

        // Update support case if caseId is provided
        if (contributionData.caseId) {
            await updateDoc(doc(db, 'supportCases', contributionData.caseId), {
                amountRaised: increment(contributionData.amount),
                contributors: arrayUnion(contributionData.userId),
                updatedAt: new Date().toISOString()
            });
        }

        return contributionRef.id;
    } catch (error) {
        console.error('Error creating contribution:', error);
        throw error;
    }
};

export const getContributionById = async (contributionId) => {
    try {
        const contributionDoc = await getDoc(doc(db, 'contributions', contributionId));
        if (!contributionDoc.exists()) {
            throw new Error('Contribution not found');
        }
        return { id: contributionDoc.id, ...contributionDoc.data() };
    } catch (error) {
        console.error('Error getting contribution:', error);
        throw error;
    }
};

// Support Case Operations
export const createSupportCase = async (caseData) => {
    const caseRef = await addDoc(collection(db, 'supportCases'), {
        ...caseData,
        createdAt: new Date().toISOString(),
        status: 'pending_verification',
        amountRaised: 0,
        contributors: []
    });
    return caseRef.id;
};

export const getActiveSupportCases = async () => {
    const q = query(
        collection(db, 'supportCases'),
        where('status', 'in', ['active', 'pending_verification']),
        orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getSupportCase = async (caseId) => {
    const caseDoc = await getDoc(doc(db, 'supportCases', caseId));
    return caseDoc.exists() ? { id: caseDoc.id, ...caseDoc.data() } : null;
};

export const updateSupportCase = async (caseId, updates) => {
    await updateDoc(doc(db, 'supportCases', caseId), {
        ...updates,
        updatedAt: new Date().toISOString()
    });
};

// Member Operations
export const getAllMembers = async () => {
    const q = query(
        collection(db, 'users'),
        where('status', '==', 'active'),
        orderBy('name')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const searchMembers = async (searchTerm) => {
    // Note: Firestore doesn't support full-text search natively
    // This is a basic implementation - consider using Algolia for better search
    const members = await getAllMembers();
    return members.filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
};

// Death Report Operations
export const createDeathReport = async (reportData) => {
    try {
        const reportRef = await addDoc(collection(db, 'deathReports'), {
            ...reportData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });

        // If this is a self-report, automatically create a support case
        if (reportData.relationship === 'self') {
            await addDoc(collection(db, 'supportCases'), {
                reportedBy: reportData.reportedBy,
                reportedByName: reportData.reportedByName,
                memberName: reportData.deceasedName,
                relationship: reportData.relationship,
                deathReportId: reportRef.id,
                amountNeeded: 1000, // Default amount
                amountRaised: 0,
                contributors: [],
                status: 'pending_verification',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        }

        return reportRef.id;
    } catch (error) {
        console.error('Error creating death report:', error);
        throw error;
    }
};

export const getDeathReports = async (userId = null) => {
    try {
        let q;
        if (userId) {
            q = query(
                collection(db, 'deathReports'),
                where('reportedBy', '==', userId),
                orderBy('createdAt', 'desc')
            );
        } else {
            q = query(
                collection(db, 'deathReports'),
                orderBy('createdAt', 'desc'),
                limit(50)
            );
        }

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Ensure we have default values
            status: doc.data().status || 'pending_review',
            createdAt: doc.data().createdAt || new Date().toISOString()
        }));
    } catch (error) {
        console.error('Error getting death reports:', error);
        throw error;
    }
};

export const updateDeathReportStatus = async (reportId, status, adminNotes = '') => {
    try {
        await updateDoc(doc(db, 'deathReports', reportId), {
            status,
            adminNotes,
            reviewedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating death report:', error);
        throw error;
    }
};

// Profile Operations
export const updateUserProfile = async (userId, updates) => {
    try {
        await updateDoc(doc(db, 'users', userId), {
            ...updates,
            updatedAt: new Date().toISOString()
        });
        return { success: true };
    } catch (error) {
        console.error('Error updating user profile:', error);
        return { success: false, error: error.message };
    }
};

export const updateUserProfilePicture = async (userId, profilePictureUrl) => {
    try {
        await updateDoc(doc(db, 'users', userId), {
            profilePictureUrl,
            updatedAt: new Date().toISOString()
        });
        return { success: true };
    } catch (error) {
        console.error('Error updating profile picture:', error);
        return { success: false, error: error.message };
    }
};

export const getUserProfile = async (userId) => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) {
            throw new Error('User not found');
        }
        return { id: userDoc.id, ...userDoc.data() };
    } catch (error) {
        console.error('Error getting user profile:', error);
        throw error;
    }
};

// Dashboard Operations
export const getDashboardStats = async (userId) => {
    try {
        const [cases, contributions, members] = await Promise.all([
            getActiveSupportCases(),
            getUserContributions(userId),
            getAllMembers()
        ]);

        const totalMembers = members.length;
        const totalContributions = members.reduce((sum, member) => sum + (member.totalContributions || 0), 0);
        const totalAmountGiven = totalContributions * 50; // Assuming $50 per contribution
        const activeCasesCount = cases.length;
        const userContributionsCount = contributions.length;

        return {
            totalMembers,
            activeCases: activeCasesCount,
            userContributions: userContributionsCount,
            totalAmountGiven,
            recentContributions: contributions.slice(0, 5)
        };
    } catch (error) {
        console.error('Error getting dashboard stats:', error);
        throw error;
    }
};

export const getRecentCommunityActivity = async (limit = 10) => {
    try {
        const q = query(
            collection(db, 'contributions'),
            orderBy('createdAt', 'desc'),
            limit(limit)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting recent activity:', error);
        throw error;
    }
};

export const getTopContributors = async (limit = 5) => {
    try {
        const q = query(
            collection(db, 'users'),
            where('totalContributions', '>', 0),
            orderBy('totalContributions', 'desc'),
            limit(limit)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            totalContributions: doc.data().totalContributions || 0,
            profilePictureUrl: doc.data().profilePictureUrl
        }));
    } catch (error) {
        console.error('Error getting top contributors:', error);
        throw error;
    }
};

// Voucher Operations
export const getUserVouchers = async (userId) => {
    try {
        const q = query(
            collection(db, 'vouchers'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Ensure we have default values
            amount: doc.data().amount || 0,
            status: doc.data().status || 'active',
            purchaseDate: doc.data().purchaseDate || doc.data().createdAt,
            expiryDate: doc.data().expiryDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        }));
    } catch (error) {
        console.error('Error getting user vouchers:', error);
        throw error;
    }
};

export const createVoucher = async (voucherData) => {
    try {
        const voucherRef = await addDoc(collection(db, 'vouchers'), voucherData);
        return voucherRef.id;
    } catch (error) {
        console.error('Error creating voucher:', error);
        throw error;
    }
};

export const updateVoucher = async (voucherId, updates) => {
    try {
        await updateDoc(doc(db, 'vouchers', voucherId), {
            ...updates,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating voucher:', error);
        throw error;
    }
};

export const useVoucher = async (voucherId, contributionData) => {
    try {
        await updateDoc(doc(db, 'vouchers', voucherId), {
            status: 'used',
            usedDate: new Date().toISOString(),
            usedFor: `Contribution for ${contributionData.caseMemberName}`,
            usedInContribution: contributionData.contributionId,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error using voucher:', error);
        throw error;
    }
};

export const getVoucherByCode = async (voucherCode) => {
    try {
        const q = query(
            collection(db, 'vouchers'),
            where('code', '==', voucherCode),
            limit(1)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
            return null;
        }
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
    } catch (error) {
        console.error('Error getting voucher by code:', error);
        throw error;
    }
};